import { useLazyQuery } from "@apollo/client/react";
import { useEffect, useMemo, useState } from "react";
import { track } from "../analytics";
import { AvailableCountry } from "../lib/DocumentType";
import {
  SearchInmatesDocument,
  SearchInmatesQuery,
} from "./SearchInmates.generated";
import { MultipleSearchCriteria, SearchCriteria, SearchResult } from "./types";

const formatResult = (
  inmate: SearchInmatesQuery["adultInmates"]["results"][number]
): SearchResult => ({
  id: inmate.id,
  fullName: inmate.fullName,
  facilityId: inmate.facility.id,
  facilityName: inmate.facility.name,
  facilityPublicId: inmate.facility.publicId,
  facilityVisitationFormLink: inmate.facility.visitationFormLink,
  facilityMinimumAgeIdRequired: inmate.facility.minimumAgeIdRequired,
  inmateIdentification: inmate.inmateIdentification,
  features: inmate.facility.features,
});

function isBatchSearch(
  search: SearchCriteria
): search is MultipleSearchCriteria {
  return "idNumbers" in search && !!search.idNumbers;
}

export function parseIdNumbers(idNumberString: string) {
  return idNumberString
    .split(/[ ,\t\n]+/)
    .filter((s, i, a) => s.trim() !== "" && a.indexOf(s) === i);
}

export function useInmateSearch(search: SearchCriteria) {
  const [loading, setLoading] = useState(true);

  const [searchInmates, { data, error }] = useLazyQuery(SearchInmatesDocument, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!data) return;
    track("Contact request - Adults searched", {
      resultSize: data.adultInmates.results.length,
      minorsOmitted: data.adultInmates.minorsOmitted,
      countryFilter: search.country,
      provinceFilter: search.province,
      facilityFilter: "facilityId" in search ? search.facilityId : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    setLoading(true);
    searchInmates({
      variables: isBatchSearch(search)
        ? {
            province: search.province || undefined,
            country: search.country || AvailableCountry.US,
            inmateIds: parseIdNumbers(search.idNumbers),
            lastNameOrInmateId: undefined,
            facilityId: undefined,
          }
        : {
            province: search.province || undefined,
            country: search.country || AvailableCountry.US,
            inmateIds: undefined,
            lastNameOrInmateId: search.lastNameOrInmateId,
            facilityId: search.facilityId,
          },
    });
  }, [search, searchInmates]);

  const results = useMemo(() => {
    const result =
      data?.adultInmates.results.map((res) => formatResult(res)) || [];
    setLoading(!data);
    return result;
  }, [data]);

  return {
    results,
    minorsOmitted: data?.adultInmates.minorsOmitted,
    loading,
    error,
  };
}
